import React from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import jpMoment from '@/shared/jp_moment';

const Calendar = ({ startOfMonth, fairDays, webReservePeriodDate, diffMonths = 3, selectedDate= [], initializeDate = '', type= ''}) => {
    const MAX_WEEK = 6;
    const MAX_WEEKLY = 7;

    return (
        <div className="calendar" id={type ? `js-calendar_${type}` : ''}>
            <div className="calendar__bg">
                <a className="calendar__close" href="#">×</a>
            </div>
            <div className="calendar__inner js-slick__list background-color--contents_bg" id="calendar__inner">
                {[...Array(diffMonths)].map((_, i) => {
                    const month = moment(startOfMonth).add(i, 'months');
                    let date = moment(startOfMonth).add(i, 'months').startOf('month').startOf('week');
                    return (
                        <div className="calendar__item background-color--contents_bg" key={i}>
                            <div className="calendar__item-head color--accent font-family--middle">
                                {month.format('Y')}/<span className="calendar__month">{month.format('MM')}</span>
                            </div>
                            <div className="calendar__item-body">
                                <ul className="calendar__week-list">
                                    {['日', '月', '火', '水', '木', '金', '土'].map((day, index) => (
                                        <li className="calendar__week-item" key={index}>{day}</li>
                                    ))}
                                </ul>
                                <ul className="calendar__day-list">
                                    {[...Array(MAX_WEEK)].map((_, weekIndex) => (
                                        [...Array(MAX_WEEKLY)].map((_, dayIndex) => {
                                            const dayId = weekIndex * MAX_WEEKLY + dayIndex;
                                            const currentDate = date.clone().add(dayId, 'days');
                                            const dayClass = calendarDayClass(month.format('MM'), currentDate, fairDays, webReservePeriodDate);
                                            const formattedDate = currentDate.format('YYYY-MM-DD');
                                            const disabled = dayClass.includes('disabled') || dayClass.includes('is-full');
                                            const isChecked = formattedDate === initializeDate && selectedDate.length === 0
                                                ? true // 初期値の日付（日付順）が存在して、カレンダーから選択された日付がないときに初期値の日付をcheckedにする
                                                : selectedDate.includes(formattedDate) // それ以外の場合、選択された日付をcheckedにする
                                            return (
                                                <li className={`calendar__day-item ${dayClass}${!isChecked ? setHolidayClass(currentDate) : ''}`} key={weekIndex-dayIndex}>
                                                    {dayClass !== 'is-empty' && (
                                                        <label className="calendar__day-check">
                                                            <input
                                                                type="checkbox"
                                                                name="date[]"
                                                                value={formattedDate}
                                                                disabled={disabled}
                                                                checked={isChecked}
                                                                data-date={`${currentDate.format('YYYY年MM月DD日')}(${weekJa(currentDate)})`}
                                                                className="calendar__day-input"
                                                                readOnly
                                                            />
                                                            <span className={`calendar__day-check-inner border-color--text-before ${isChecked ? 'background-color--sub' : ''}`}>{currentDate.format('D')}</span>
                                                        </label>
                                                    )}
                                                </li>
                                            );
                                        })
                                    ))}
                                </ul>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

const calendarDayClass = (month, date, fairDays, webReservePeriodDate) => {
    let dayClass = 'is-disabled';
    const toDay = moment().format("YYYY-MM-DD");
    let reservableDay = moment(toDay);
    const currentDate = date.format('YYYY-MM-DD');

    if (webReservePeriodDate > 0) {
        reservableDay = reservableDay.add(webReservePeriodDate, 'days');
    }

    // カレンダーの月ではない場合
    if (date.format('MM') !== month) {
        dayClass = 'is-empty';
        return dayClass;
    }

    // 予約可能な日を過ぎた場合
    if (date.isBefore(reservableDay)) {
        return dayClass;
    }

    if (fairDays[currentDate]) {
        // 日付ごとの残席状況表示
        for(const fairDay of Object.values(fairDays[currentDate])) {
            const existsFreeTime = fairDay.times.some(time => time.remainder == 0);
            const existsFewTime = fairDay.times.some(time => time.remainder == 1);

            // 空席が1つ以上あるとき
            if (existsFreeTime) {
                dayClass = 'is-accepting';
                break;
            } else if (existsFewTime) {
                // 空席がなく、混雑が1つ以上あるとき
                dayClass = 'is-few';
                break;
            } else {
                dayClass = 'is-full';
            }
        }
    }

    return dayClass;
};

// 土日祝日のクラスを設定
const setHolidayClass = (date) => {
    let holidayClass = ' background-color--page_bg';

    if (jpMoment(date)._isHoliday() || date.day() / 6 === 0) {
        holidayClass = ' is-holiday background-color--calendar_sun_bg'; // 日曜日
    } else if (date.day() / 6 === 1) {
        holidayClass = ' is-sat background-color--calendar_sat_bg'; // 土曜日
    }
    return holidayClass;
}

const weekJa = (date) => {
    const weekDays = ['日', '月', '火', '水', '木', '金', '土'];
    return weekDays[date.day()];
};

/**
 * startOfMonth: カレンダー開始日(YYYY-mm-dd)
 * fairDays: 開催日付と時間一覧
 * webReservePeriodDate: オンライン予約の受付期限（日数）
 * diffMonths: カレンダーの表示月
 * selectedDate: 選択されている日付
 * initializeDate: 初期値の日付データ（日付順）
 * type: カレンダータイプ
 */
Calendar.propTypes = {
    startOfMonth: PropTypes.string.isRequired,
    fairDays: PropTypes.object.isRequired,
    webReservePeriodDate: PropTypes.string.isRequired,
    diffMonths: PropTypes.number,
    selectedDate: PropTypes.array,
    initializeDate: PropTypes.string,
    type: PropTypes.string,
};

export default Calendar;
